import React, { useRef, useEffect } from 'react';
import { Bold, Italic, Underline, Type, Image, List, ListOrdered } from 'lucide-react';

const RichTextEditor = ({ value, onChange, placeholder = "Enter text...", rows = 4, maxWords = null }) => {
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);

  // Initialize content
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, []);

  // Store last valid value for restoration if limit exceeded
  const lastValidValue = useRef(value || '');
  
  useEffect(() => {
    lastValidValue.current = value || '';
  }, [value]);
  
  // Helper function to count words from HTML
  const countWords = (html) => {
    const plainText = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    return plainText.split(' ').filter(word => word.length > 0).length;
  };

  const handleInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      
      // Enforce word limit if specified
      if (maxWords) {
        const wordCount = countWords(html);
        if (wordCount > maxWords) {
          // Restore last valid value without destroying formatting
          editorRef.current.innerHTML = lastValidValue.current;
          return;
        }
      }
      
      lastValidValue.current = html;
      onChange(html);
    }
  };

  const execCommand = (command, value = null) => {
    if (command === 'insertUnorderedList' || command === 'insertOrderedList') {
      // Special handling for lists to ensure they work properly
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const selectedText = range.toString() || 'List item';
        
        // Create list elements
        const listElement = command === 'insertUnorderedList' ? 'ul' : 'ol';
        const listItem = document.createElement('li');
        listItem.textContent = selectedText;
        
        const list = document.createElement(listElement);
        list.appendChild(listItem);
        
        // Replace selection with list
        range.deleteContents();
        range.insertNode(list);
        
        // Move cursor to end of list item
        const newRange = document.createRange();
        newRange.selectNodeContents(listItem);
        newRange.collapse(false);
        selection.removeAllRanges();
        selection.addRange(newRange);
        
        handleInput();
        return;
      }
    }
    
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imgSrc = e.target.result;
        insertImage(imgSrc, file.name);
      };
      reader.readAsDataURL(file);
    }
  };

  const insertImage = (imgSrc, altText = 'Image') => {
    if (editorRef.current) {
      // Create image with responsive styling
      const imgHtml = `<img src="${imgSrc}" alt="${altText}" style="max-width: 100%; height: auto; border-radius: 8px; margin: 10px 0;" />`;
      document.execCommand('insertHTML', false, imgHtml);
      handleInput();
    }
  };

  const triggerImageUpload = () => {
    imageInputRef.current?.click();
  };

  const toolbarButtons = [
    { icon: Bold, command: 'bold', title: 'Bold' },
    { icon: Italic, command: 'italic', title: 'Italic' },
    { icon: Underline, command: 'underline', title: 'Underline' },
  ];

  return (
    <>
      <style jsx>{`
        .rich-text-editor ul {
          margin-left: 20px;
          padding-left: 20px;
          margin: 10px 0;
          list-style-type: disc;
        }
        .rich-text-editor ol {
          margin-left: 20px;
          padding-left: 20px;
          margin: 10px 0;
          list-style-type: decimal;
        }
        .rich-text-editor li {
          margin: 5px 0;
          line-height: 1.5;
        }
      `}</style>
      <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500">
      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-300 px-3 py-2 flex items-center space-x-1">
        <div className="flex items-center space-x-1">
          {toolbarButtons.map((btn) => (
            <button
              key={btn.command}
              type="button"
              onClick={() => execCommand(btn.command)}
              className="p-1.5 rounded hover:bg-gray-200 transition-colors"
              title={btn.title}
            >
              <btn.icon className="h-4 w-4 text-gray-600" />
            </button>
          ))}
        </div>
        
        <div className="h-5 w-px bg-gray-300 mx-2" />
        
        {/* List buttons */}
        <div className="flex items-center space-x-1">
          <button
            type="button"
            onClick={() => execCommand('insertUnorderedList')}
            className="p-1.5 rounded hover:bg-gray-200 transition-colors"
            title="Bullet List"
          >
            <List className="h-4 w-4 text-gray-600" />
          </button>
          <button
            type="button"
            onClick={() => execCommand('insertOrderedList')}
            className="p-1.5 rounded hover:bg-gray-200 transition-colors"
            title="Numbered List"
          >
            <ListOrdered className="h-4 w-4 text-gray-600" />
          </button>
        </div>
        
        <div className="h-5 w-px bg-gray-300 mx-2" />
        
        {/* Image button */}
        <div className="flex items-center space-x-1">
          <button
            type="button"
            onClick={triggerImageUpload}
            className="p-1.5 rounded hover:bg-gray-200 transition-colors"
            title="Insert Image"
          >
            <Image className="h-4 w-4 text-gray-600" />
          </button>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
        </div>
        
        <div className="h-5 w-px bg-gray-300 mx-2" />
        <span className="text-xs text-gray-500">Supports HTML formatting, lists & images</span>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={(e) => {
          // Handle Enter key for lists
          if (e.key === 'Enter') {
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              const currentNode = range.startContainer;
              const listItem = currentNode.nodeType === Node.TEXT_NODE ? currentNode.parentElement : currentNode;
              
              // If we're in a list item, create a new list item
              if (listItem.tagName === 'LI') {
                e.preventDefault();
                const newListItem = document.createElement('li');
                newListItem.innerHTML = '<br>';
                listItem.parentNode.insertBefore(newListItem, listItem.nextSibling);
                
                // Move cursor to new list item
                const newRange = document.createRange();
                newRange.selectNodeContents(newListItem);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);
                
                handleInput();
                return;
              }
            }
            
            e.preventDefault();
            document.execCommand('insertParagraph', false, null);
            handleInput();
          }
          
          // Block typing if at word limit (allow backspace, delete, arrows)
          if (maxWords && editorRef.current) {
            const html = editorRef.current.innerHTML;
            const wordCount = countWords(html);
            const isAtLimit = wordCount >= maxWords;
            const isNavigationKey = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key);
            const isEditKey = ['Backspace', 'Delete', 'Ctrl', 'Meta', 'Alt', 'Shift'].includes(e.key) || e.ctrlKey || e.metaKey;
            const isNewWord = e.key === ' ' || (e.key.length === 1 && !e.key.match(/\s/));
            
            if (isAtLimit && isNewWord && !isEditKey && !isNavigationKey) {
              e.preventDefault();
              return;
            }
          }
        }}
        className="w-full px-3 py-2 min-h-[100px] outline-none resize-y overflow-auto rich-text-editor"
        style={{ 
          minHeight: `${rows * 24}px`
        }}
        placeholder={placeholder}
        onPaste={(e) => {
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          
          // Check word limit before pasting
          if (maxWords && editorRef.current) {
            const currentHtml = editorRef.current.innerHTML;
            const currentWordCount = countWords(currentHtml);
            const normalizedPaste = text.trim().replace(/\s+/g, ' ');
            const pasteWords = normalizedPaste.split(' ').filter(word => word.length > 0);
            const remainingWords = maxWords - currentWordCount;
            
            if (remainingWords <= 0) {
              return; // No space for more words
            }
            
            const allowedPaste = pasteWords.slice(0, remainingWords).join(' ');
            document.execCommand('insertText', false, allowedPaste);
          } else {
            document.execCommand('insertText', false, text);
          }
          handleInput();
        }}
      />
      </div>
    </>
  );
};

export default RichTextEditor;
